import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createClinicAndTherapist,
  seedTreatmentTypes,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
  getTherapistWithClinic,
  updateTherapistDocument,
  updateClinicDocument,
  uploadClinicLogo,
  getClinicTherapists,
  getActiveSystemMessages,
  acknowledgeSystemMessage,
  getFeatureTips,
  dismissFeatureTip,
  updateTherapistStatus,
  getPatientCountsByTherapist,
  functions,
} from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { serializeForRedux } from "../../utils/dateUtils";

export const therapistApi = createApi({
  reducerPath: "therapistApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Therapist", "Clinic", "SystemMessages", "FeatureTips"],
  endpoints: (builder) => ({
    // Get therapist with clinic data
    getTherapistProfile: builder.query({
      async queryFn(uid) {
        try {
          const data = await getTherapistWithClinic(uid);
          if (!data) {
            console.error("[CK-7030] Therapist not found:", {
              errorCode: "CK-7030",
              therapistUid: uid,
              timestamp: new Date().toISOString(),
            });
            return { error: { message: "Therapist not found" } };
          }
          return { data };
        } catch (error) {
          console.error("[CK-7031] Error fetching therapist profile:", {
            errorCode: "CK-7031",
            errorMessage: error.message,
            therapistUid: uid,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, uid) => [
        { type: "Therapist", id: uid },
        { type: "Clinic", id: result?.clinic?.id },
      ],
    }),

    // Create new therapist and clinic (registration)
    createTherapistAndClinic: builder.mutation({
      async queryFn({ uid, userData }) {
        try {
          // Create therapist and clinic documents
          const result = await createClinicAndTherapist(uid, userData);

          // Seed treatment types chosen in the preferences step.
          // Non-blocking: signup must never fail because of categories —
          // the therapist can add them in Settings → התאמה אישית.
          if (Array.isArray(userData.treatmentTypes) && userData.treatmentTypes.length > 0) {
            try {
              const created = await seedTreatmentTypes(
                result.clinic.id,
                userData.treatmentTypes
              );
              created.forEach((category) => {
                logActivity(result.clinic.id, {
                  therapistId: uid,
                  therapistName: userData.fullName || "לא ידוע",
                  action: ACTIVITY_ACTIONS.CREATE_CATEGORY,
                  entityType: ENTITY_TYPES.CATEGORY,
                  entityId: category.id,
                  entityName: category.name,
                  details: { type: "treatmentType", color: category.color, source: "signup" },
                  success: true,
                });
              });
            } catch (seedError) {
              console.error("[CK-7055] Error seeding treatment types at signup:", {
                errorCode: "CK-7055",
                errorMessage: seedError.message,
                clinicId: result.clinic.id,
                count: userData.treatmentTypes.length,
                timestamp: new Date().toISOString(),
              });
              logActivity(result.clinic.id, {
                therapistId: uid,
                therapistName: userData.fullName || "לא ידוע",
                action: ACTIVITY_ACTIONS.CREATE_CATEGORY,
                entityType: ENTITY_TYPES.CATEGORY,
                entityName: "סוגי טיפול (הרשמה)",
                success: false,
                errorMessage: seedError.message,
              });
            }
          }

          // Upload logo if provided (after clinic is created)
          if (userData.logoFile) {
            const logoUrl = await uploadClinicLogo(
              userData.logoFile,
              result.clinic.id
            );
            await updateClinicDocument(result.clinic.id, { logoUrl });
            result.clinic.logoUrl = logoUrl;
          }

          return { data: result };
        } catch (error) {
          console.error("[CK-7032] Error creating therapist and clinic:", {
            errorCode: "CK-7032",
            errorMessage: error.message,
            therapistUid: uid,
            emailMasked: userData?.email
              ? `${userData.email.slice(0, 3)}***`
              : null,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { uid }) => [
        { type: "Therapist", id: uid },
      ],
    }),

    // Update therapist profile
    updateTherapist: builder.mutation({
      async queryFn({ uid, data }) {
        try {
          await updateTherapistDocument(uid, data);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7033] Error updating therapist:", {
            errorCode: "CK-7033",
            errorMessage: error.message,
            therapistUid: uid,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { uid }) => [
        { type: "Therapist", id: uid },
      ],
    }),

    // Update clinic
    updateClinic: builder.mutation({
      async queryFn({ clinicId, data }) {
        try {
          await updateClinicDocument(clinicId, data);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7034] Error updating clinic:", {
            errorCode: "CK-7034",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        { type: "Clinic", id: clinicId },
      ],
    }),

    // Upload clinic logo
    uploadLogo: builder.mutation({
      async queryFn({ clinicId, file }) {
        try {
          const logoUrl = await uploadClinicLogo(file, clinicId);
          await updateClinicDocument(clinicId, { logoUrl });
          return { data: { logoUrl } };
        } catch (error) {
          console.error("[CK-7035] Error uploading clinic logo:", {
            errorCode: "CK-7035",
            errorMessage: error.message,
            clinicId,
            fileName: file?.name,
            fileSize: file?.size,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        { type: "Clinic", id: clinicId },
      ],
    }),

    // ======================================================================
    // MULTI-CLINIC v2 — team management (Phase 1). All invalidate CLINIC_LIST
    // so every therapist picker in the app refreshes.
    // ======================================================================

    // Add a team member (Auth user + therapist doc) — server callable.
    createTeamMember: builder.mutation({
      async queryFn({ clinicId, firstName, lastName, email, phone, clinicRole = "therapist", permissions = null, sendInviteSms = true, inviteNote = "" }) {
        try {
          const fn = httpsCallable(functions, "createTherapistUnderClinic");
          const res = await fn({
            clinicId, firstName, lastName, email, phone, clinicRole,
            permissions: permissions || undefined,
            sendInviteSms: sendInviteSms !== false,
            inviteNote: inviteNote ? String(inviteNote).slice(0, 120) : undefined,
          });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7125] Error creating team member:", {
            errorCode: "CK-7125",
            errorMessage: error.message,
            functionCode: error.code,
            clinicId,
            clinicRole,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: (r, e, { clinicId }) => [
        { type: "Therapist", id: "CLINIC_LIST" },
        { type: "Clinic", id: clinicId },
      ],
    }),

    // Change clinicRole + per-therapist permissions — server callable.
    updateTherapistRole: builder.mutation({
      async queryFn({ clinicId, therapistId, clinicRole, permissions, overriddenKeys = [], expectedPermissionsUpdatedAt }) {
        try {
          const fn = httpsCallable(functions, "updateTherapistRole");
          const res = await fn({
            clinicId,
            therapistId,
            clinicRole,
            permissions,
            overriddenKeys,
            // Optimistic-concurrency token (ms epoch | null). Omitted ⇒ no check.
            ...(expectedPermissionsUpdatedAt !== undefined ? { expectedPermissionsUpdatedAt } : {}),
          });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7126] Error updating therapist role:", {
            errorCode: "CK-7126",
            errorMessage: error.message,
            functionCode: error.code,
            clinicId,
            targetTherapistId: therapistId,
            clinicRole,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: (r, e, { therapistId }) => [
        { type: "Therapist", id: "CLINIC_LIST" },
        { type: "Therapist", id: therapistId },
      ],
    }),

    // Re-send the invite SMS (admin; server rate-limits 1/24h, max 5).
    resendTeamInvite: builder.mutation({
      async queryFn({ clinicId, therapistId }) {
        try {
          const fn = httpsCallable(functions, "resendTeamInvite");
          const res = await fn({ clinicId, therapistId });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7130] Error resending team invite:", {
            errorCode: "CK-7130",
            errorMessage: error.message,
            functionCode: error.code,
            clinicId,
            targetTherapistId: therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: [{ type: "Therapist", id: "CLINIC_LIST" }],
    }),

    // First login of an invited member (self): pending → accepted.
    markInviteAccepted: builder.mutation({
      async queryFn({ clinicId }) {
        try {
          const fn = httpsCallable(functions, "markInviteAccepted");
          const res = await fn({ clinicId });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7131] Error marking invite accepted:", {
            errorCode: "CK-7131",
            errorMessage: error.message,
            functionCode: error.code,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: [{ type: "Therapist", id: "CLINIC_LIST" }],
    }),

    // Deactivate / reactivate (client write; rules allow same-clinic admins).
    setTherapistStatus: builder.mutation({
      async queryFn({ clinicId, therapistId, isActive, actorId, actorName, targetName }) {
        try {
          await updateTherapistStatus(therapistId, isActive);
          logActivity(clinicId, {
            therapistId: actorId,
            therapistName: actorName || null,
            action: ACTIVITY_ACTIONS.UPDATE_THERAPIST_STATUS,
            entityType: ENTITY_TYPES.THERAPIST,
            entityId: therapistId,
            entityName: targetName || null,
            details: { isActive },
            success: true,
          });
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7127] Error updating therapist status:", {
            errorCode: "CK-7127",
            errorMessage: error.message,
            clinicId,
            targetTherapistId: therapistId,
            isActive,
            timestamp: new Date().toISOString(),
          });
          logActivity(clinicId, {
            therapistId: actorId,
            therapistName: actorName || null,
            action: ACTIVITY_ACTIONS.UPDATE_THERAPIST_STATUS,
            entityType: ENTITY_TYPES.THERAPIST,
            entityId: therapistId,
            details: { isActive },
            success: false,
            errorMessage: error.message,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (r, e, { clinicId }) => [
        { type: "Therapist", id: "CLINIC_LIST" },
        { type: "Clinic", id: clinicId },
      ],
    }),

    // Clinic-wide patient sharing mode ("assigned" | "all") + whether "all"
    // includes treatment reports — server callable (also auto-sets
    // calendar.viewClinic on therapists without a personal override).
    updatePatientSharingMode: builder.mutation({
      async queryFn({ clinicId, patientSharingMode, allSharedIncludesTreatments }) {
        try {
          const fn = httpsCallable(functions, "updatePatientSharingMode");
          const res = await fn({
            clinicId,
            patientSharingMode,
            ...(typeof allSharedIncludesTreatments === "boolean"
              ? { allSharedIncludesTreatments }
              : {}),
          });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7133] Error updating patient sharing mode:", {
            errorCode: "CK-7133",
            errorMessage: error.message,
            functionCode: error.code,
            clinicId,
            patientSharingMode,
            allSharedIncludesTreatments,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: (r, e, { clinicId }) => [
        { type: "Therapist", id: "CLINIC_LIST" },
        { type: "Clinic", id: clinicId },
      ],
    }),

    // Transfer (all or a subset of) patients between therapists — server callable.
    transferPatients: builder.mutation({
      async queryFn({ clinicId, fromTherapistId, toTherapistId, patientIds = null, transferFutureAppointments = true }) {
        try {
          const fn = httpsCallable(functions, "transferPatientsToTherapist");
          const res = await fn({
            clinicId,
            fromTherapistId,
            toTherapistId,
            ...(Array.isArray(patientIds) && patientIds.length > 0 ? { patientIds } : {}),
            transferFutureAppointments,
          });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7128] Error transferring patients:", {
            errorCode: "CK-7128",
            errorMessage: error.message,
            functionCode: error.code,
            clinicId,
            fromTherapistId,
            toTherapistId,
            patientCount: Array.isArray(patientIds) ? patientIds.length : "all",
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: [{ type: "Therapist", id: "CLINIC_LIST" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: "patientsApi/invalidateTags", payload: [{ type: "Patient", id: "LIST" }, { type: "Patients", id: "LIST" }] });
          dispatch({ type: "calendarApi/invalidateTags", payload: [{ type: "Appointments", id: "LIST" }] });
        } catch { /* mutation failed */ }
      },
    }),

    // Primary-patient counts per therapist (Team page). Invalidated with the list.
    getTeamPatientCounts: builder.query({
      async queryFn({ clinicId, therapistIds }) {
        try {
          const counts = await getPatientCountsByTherapist(clinicId, therapistIds || []);
          return { data: counts };
        } catch (error) {
          console.error("[CK-7129] Error fetching team patient counts:", {
            errorCode: "CK-7129",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "Therapist", id: "CLINIC_LIST" }],
    }),

    // Get all therapists for a clinic (admin only)
    getClinicTherapists: builder.query({
      async queryFn({ clinicId }) {
        try {
          const therapists = await getClinicTherapists(clinicId);
          return { data: therapists };
        } catch (error) {
          console.error("[CK-7036] Error fetching clinic therapists:", {
            errorCode: "CK-7036",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { clinicId }) => [
        { type: "Therapist", id: "CLINIC_LIST" },
      ],
    }),

    // Get all active system messages (NiV broadcasts shown as entry popups)
    getActiveSystemMessages: builder.query({
      async queryFn() {
        try {
          const messages = await getActiveSystemMessages();
          return { data: messages };
        } catch (error) {
          console.error("[CK-7037] Error fetching active system messages:", {
            errorCode: "CK-7037",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["SystemMessages"],
    }),

    // Acknowledge a system message for the current therapist
    acknowledgeSystemMessage: builder.mutation({
      async queryFn({ uid, messageId }) {
        try {
          await acknowledgeSystemMessage(uid, messageId);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7038] Error acknowledging system message:", {
            errorCode: "CK-7038",
            errorMessage: error.message,
            messageId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { uid }) => [
        { type: "Therapist", id: uid },
      ],
    }),

    // Server-computed unused-feature tips for the clinic (nightly function)
    getFeatureTips: builder.query({
      async queryFn(clinicId) {
        try {
          const tips = await getFeatureTips(clinicId);
          return { data: serializeForRedux(tips) };
        } catch (error) {
          console.error("[CK-7039] Error fetching feature tips:", {
            errorCode: "CK-7039",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "FeatureTips", id: "LIST" }],
    }),

    // Dismiss a feature tip (persisted on the therapist doc, cross-device)
    dismissFeatureTip: builder.mutation({
      async queryFn({ uid, featureKey, permanent = false }) {
        try {
          await dismissFeatureTip(uid, featureKey, { permanent });
          return { data: { success: true, featureKey, permanent } };
        } catch (error) {
          console.error("[CK-7040] Error dismissing feature tip:", {
            errorCode: "CK-7040",
            errorMessage: error.message,
            featureKey,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { uid }) => [
        { type: "Therapist", id: uid },
      ],
    }),
  }),
});

export const {
  useGetTherapistProfileQuery,
  useCreateTherapistAndClinicMutation,
  useUpdateTherapistMutation,
  useUpdateClinicMutation,
  useUploadLogoMutation,
  useGetClinicTherapistsQuery,
  useCreateTeamMemberMutation,
  useUpdateTherapistRoleMutation,
  useSetTherapistStatusMutation,
  useTransferPatientsMutation,
  useUpdatePatientSharingModeMutation,
  useGetTeamPatientCountsQuery,
  useResendTeamInviteMutation,
  useMarkInviteAcceptedMutation,
  useGetActiveSystemMessagesQuery,
  useAcknowledgeSystemMessageMutation,
  useGetFeatureTipsQuery,
  useDismissFeatureTipMutation,
} = therapistApi;
